'use client';

import { useEffect, useState } from 'react';
import {
  isChunkLoadError,
  recoverFromChunkLoadError
} from '#/lib/chunk-load-recovery';
import { reportClientError, toReport } from '#/lib/report-client-error';

export default function GlobalError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // 청크 로드 실패는 오류 화면 대신 "다시 불러오는 중" 을 보여 준다 — 모바일 회선의
  // 일시 실패라 새로고침 한 번이면 대부분 회복된다. (#273)
  const [reloading, setReloading] = useState(() => isChunkLoadError(error));

  useEffect(() => {
    console.error('[global-error]', error);

    if (isChunkLoadError(error)) {
      if (recoverFromChunkLoadError()) return; // 회복 중 — 챗에 알리지 않는다
      // 새로고침으로도 못 살아난 경우만 알린다. 스코프를 chunk-load 로 고정해
      // 어느 바운더리로 들어왔든 챗에서 같은 오류로 묶이게 한다.
      setReloading(false);
      reportClientError(toReport('chunk-load', error));
      return;
    }

    // 최상위 바운더리까지 올라온 오류 = 사용자가 페이지를 아예 못 쓴 상황이다.
    // 반드시 실시간으로 알린다. (#262)
    reportClientError(toReport('global-error', error));
  }, [error]);

  return (
    <html lang="ko">
      <head>
        <title>바른AI — 한국어 맞춤법 검사기</title>
        <meta
          name="description"
          content="바른AI는 한국어 맞춤법·띄어쓰기·문법을 AI로 실시간 교정하고, 형태소 분석·분절·사용자 사전을 지원하는 무료 한국어 맞춤법 검사기입니다."
        />
      </head>
      <body
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          fontFamily: 'system-ui, sans-serif',
          gap: '1rem'
        }}
      >
        {reloading ? (
          <>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
              페이지를 다시 불러오는 중입니다
            </h1>
            <p style={{ color: '#666' }}>
              연결이 잠시 불안정했습니다. 입력한 글은 그대로 유지됩니다.
            </p>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: '1.25rem', fontWeight: 600 }}>
              일시적인 오류가 발생했습니다
            </h1>
            <p style={{ color: '#666' }}>잠시 후 다시 시도해 주세요.</p>
            <button
              onClick={() =>
                // 청크 오류는 리렌더(reset)가 아니라 새로고침이어야 새 청크를
                // 다시 받아 온다.
                isChunkLoadError(error) ? window.location.reload() : reset()
              }
              style={{
                padding: '0.5rem 1.5rem',
                borderRadius: '0.5rem',
                border: '1px solid #ddd',
                cursor: 'pointer',
                background: '#104573',
                color: '#fff'
              }}
            >
              다시 시도
            </button>
          </>
        )}
      </body>
    </html>
  );
}
